import * as React from 'react';
import styled from 'styled-components';
import MenuIcon from '@material-ui/icons/Menu';

import HeadLine from '../organisms/HeadLine';
import useUserRole from '../hooks/useUserRole';
import ResponsiveSidebar from './ResponsiveSidebar/ResponsiveSidebar';
import { ROUTES } from '../constants/routes';
import { getEpicVersion } from '../api/dashboard';

const DashboardLayout = ({
  children,
  isCentered,
  overY,
  NoSubMenu = false,
}: Props) => {
  const [version, setVersion] = React.useState(null);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const { isEmployee, isInspector } = useUserRole();

  React.useEffect(() => {
    getEpicVersion().then(versionRes => setVersion(versionRes?.data[0]?.version));
    if (
      window.location.pathname !== ROUTES.SEARCH &&
      window.location.pathname.split('/')[1] !== 'permit'
    ) {
      localStorage.removeItem('search');
    }
  }, []);

  const closeSidebar = () => setSidebarOpen(false);
  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  return (
    <Shell>
      <MobileHamburger
        type="button"
        aria-label="Open navigation"
        onClick={toggleSidebar}
      >
        <MenuIcon />
      </MobileHamburger>
      {sidebarOpen && <Backdrop onClick={closeSidebar} />}
      <SidebarContainer $open={sidebarOpen} onClick={closeSidebar}>
        <ResponsiveSidebar
          isEmployee={isEmployee}
          isInspector={isInspector}
        />
      </SidebarContainer>
      <MainSection>
        <HeadLine NoSubMenu={NoSubMenu} />
        <Content isCentered={isCentered} overY={overY}>
          {children}
          <VersionStamp>Version {version}</VersionStamp>
        </Content>
      </MainSection>
    </Shell>
  );
};

const Shell = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  min-height: 100vh;
  max-width: 100vw;
  overflow: hidden;
  background: var(--app-bg, var(--neutral-50));
`;

const SidebarContainer = styled.div<{ $open: boolean }>`
  display: flex;
  flex-shrink: 0;

  @media only screen and (max-width: 840px) {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 1202;
    transform: ${({ $open }) => ($open ? 'translateX(0)' : 'translateX(-100%)')};
    transition: transform 0.25s ease;
    box-shadow: ${({ $open }) => ($open ? '2px 0 16px rgba(15, 23, 42, 0.2)' : 'none')};
  }
`;

const Backdrop = styled.div`
  display: none;

  @media only screen and (max-width: 840px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(15, 23, 42, 0.45);
    z-index: 1201;
  }
`;

const MobileHamburger = styled.button`
  display: none;

  @media only screen and (max-width: 840px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 12px;
    left: 12px;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: var(--neutral-0);
    border: 1px solid var(--border);
    box-shadow: var(--shadow-sm);
    color: var(--neutral-700);
    cursor: pointer;
    z-index: 1200;
    padding: 0;

    svg {
      width: 22px;
      height: 22px;
    }

    &:hover {
      background: var(--neutral-50);
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
    }
  }
`;

const MainSection = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: hidden;
  background: var(--app-bg, var(--neutral-50));
  height: 100vh;
`;

// eslint-disable-next-line
const Content = styled(
  ({ isCentered, overY, ...props }) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div {...props} />
  ),
).attrs({
  className: 'scrollable',
})`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding: 0;
  overflow-y: auto;
  overflow-x: hidden;
  ${({ isCentered }) =>
    isCentered &&
    `
    justify-content: center;
  `}

  @media print {
    margin: 0;
    padding: 0;
  }
`;

const VersionStamp = styled.p`
  position: absolute;
  bottom: -10px;
  right: var(--space-4);
  font-size: var(--fs-sm);
  color: var(--neutral-400);
`;

interface Props {
  children: React.ReactNode | React.ReactNodeArray;
  isCentered?: boolean;
  overY?: boolean;
  NoSubMenu?: boolean;
}

export default DashboardLayout;
